import { RootState, RootModel, StoreModule } from 'store/modules/root'
import {
  getSnapshot,
  applySnapshot,
  SnapshotOut,
  Instance,
} from 'mobx-state-tree'
import { runInAction } from 'mobx'

export const applyState = (
  store: Instance<RootModel>,
  state: { [key in StoreModule]?: Record<string, unknown> },
  currentUserId?: string | null,
) => {
  const existing = getSnapshot<SnapshotOut<RootState>>(store)
  const newKeys = Object.keys(state) as Exclude<StoreModule, '_meta'>[]
  const newState = newKeys.reduce((acc, key) => {
    const module = acc[key]
    if (!module || !('data' in module)) return acc
    const existingData = module.data as Record<string, unknown>
    const newData = state[key] as Record<string, unknown>
    // Merge per-resource so that relationships omitted from the new response
    // (e.g. `linkage always: false` in the Rails serializer, which yields
    // `{ meta: { included: false } }` without a `data` key) preserve their
    // existing values instead of being wiped to defaults by `applySnapshot`.
    // The deserializer already skips relationships without a `data` key, so
    // they won't appear on `newResource`; here we keep the prior value.
    const mergedData: Record<string, unknown> = { ...existingData }
    for (const id in newData) {
      const existingResource = existingData[id] as
        Record<string, unknown> | undefined
      const newResource = newData[id] as Record<string, unknown>
      mergedData[id] = existingResource
        ? { ...existingResource, ...newResource }
        : newResource
    }
    return {
      ...acc,
      [key]: {
        ...module,
        data: mergedData,
      },
    }
  }, existing)

  // Always include currentUser in the snapshot if provided, so it's set atomically.
  // The reference will resolve once the user data is loaded by applySnapshot.
  if (currentUserId !== undefined && currentUserId !== null) {
    newState.currentUser = currentUserId
  }

  // Use runInAction to batch the state application and currentUser setting.
  // This ensures reactive code doesn't run between applySnapshot and setCurrentUser,
  // preventing race conditions where nonNullCurrentUser is accessed before it's set.
  runInAction(() => {
    // Apply the state (this loads the user data and sets currentUser atomically)
    applySnapshot(store, newState)

    // After applySnapshot, ensure the reference is set and resolves correctly.
    // We set it explicitly to ensure the reference resolves immediately after
    // the user data is loaded, before any reactive code can access it.
    if (currentUserId !== undefined && currentUserId !== null) {
      // Verify user exists in store
      if (store.users.data.has(currentUserId)) {
        // Set the reference explicitly to ensure it resolves
        // This is safe because the user data already exists
        store.setCurrentUser(currentUserId)
      } else {
        console.warn(
          `applyState: User ${currentUserId} not found in store after applySnapshot. ` +
            `Available users: ${Array.from(store.users.data.keys()).join(', ')}`,
        )
      }
    }
  })
}
